import { Redirect, RouteComponentProps } from 'react-router-dom';

import { Engine_Type } from '../../aiem/gen/ai/h2o/engine/v1/engine_pb';
import { AIEMUserActionType } from '../../aiem/userActions';
import { AuditTrailUserActionType } from '../../audit-trail/userActions';
import { Permissions } from '../../authz/providers/RoleProvider';
import { WorkspacesUserActionType } from '../../authz/userActions';
import { NavContext } from '../../components/Navigation/navigationItems';
import { DriveUserActionType } from '../../drive/userActions';
import { CloudDiscoveryKey, EnvironmentAndMenu } from '../../utils/contexts';
import { useEnv, useUser } from '../../utils/hooks';
import AdminAIEnginesPage from '../AdminAIEnginesPage';
import AdminAppsPage from '../AdminAppsPage';
import AdminInstancePage from '../AdminInstancePage';
import AdminManageAliasPage from '../AdminManageAliasPage';
import AdminManageSecretsPage from '../AdminManageSecretsPage';
import { AdminMarketplaceAppDetailPage } from '../AdminMarketplace/AdminMarketplaceAppDetailPage/AdminMarketplaceAppDetailPage';
import AdminMarketplaceAppListPage from '../AdminMarketplace/AdminMarketplaceAppListPage/AdminMarketplaceAppListPage';
import AIEMAdminSettingsPage from '../AIEngineSettingsPage/AIEMAdminSettingsPage';
import AppAlias from '../AppAlias/AppAlias';
import AppDetailPage from '../AppDetailsPage/AppDetailPage';
import AppInstanceLogPage from '../AppInstanceLogPage/AppInstanceLogPage';
import AppStorePage from '../AppStorePage/AppStorePage';
import { AuditTrailPage } from '../AuditTrailPage/AuditTrailPage';
import CliAndApiAccessPage from '../CliAndApiAccessPage/CliAndApiAccessPage';
import { Drive } from '../Drive/Drive';
import { HomePage } from '../HomePage/HomePage';
import InstancePage from '../InstancePage';
import LegacyEngineLogPage from '../LegacyEngineLogPage/LegacyEngineLogPage';
import LoggingServicesPage from '../LoggingServicesPage/LoggingServicesPage';
import { AdminWorkspaces } from '../ManageWorkspaces/AdminWorkspaces';
import { MyWorkspaces } from '../ManageWorkspaces/MyWorkspaces';
import MyAIEnginesPage from '../MyAIEnginesPage';
import MyAppsPage from '../MyAppsPage';
import NotebookAdminSettingsPage from '../NotebookAdminSettingsPage/NotebookAdminSettingsPage';
import PlatformUsage from '../PlatformUsage/PlatformUsage';
import { type RoutePath, RoutePaths } from '../RoutePaths';
import OAuthClientsPage from '../SecureStoreAdmin/OAuthClientsPage';
import SecretServicePage from '../SecureStoreAdmin/SecretServicePage';
import { ADMIN_CENTER_ROUTE_DEFINITIONS } from './adminCenterRouteDefinitions';
import { FEATURESTORE_ROUTE_DEFINITIONS } from './featurestoreRouteDefinitions';
import { MLOPS_ROUTE_DEFINITIONS } from './mlopsRouteDefinitions';
import { ORCHESTRATOR_ROUTE_DEFINITIONS } from './orchestratorRouteDefinitions';

/** Permission with action and optional resource for authz checks.
 * If resource is undefined, the dynamic workspace resource is used. */
export type AuthzPermission = { action: string; resource?: string };

/** Create permission objects from action strings (uses dynamic workspace resource) */
const createPermissions = (actions: string[]): AuthzPermission[] => actions.map((action) => ({ action }));

export interface RouteDefinition {
  path: RoutePath;
  render: (props: any) => JSX.Element;
  exact?: boolean;
  requiresAuth?: boolean; // If true, user must be authenticated
  requiresAdmin?: boolean; // If true, user must be admin (automatically implies requiresAuth)
  requiresEnvCondition?: (env: EnvironmentAndMenu) => boolean; // Optional function to check environment-based permissions
  requiresWorkspaces?: boolean; // If true, route requires workspace context (enables workspace dropdown)
  requiresDiscoveryKey?: CloudDiscoveryKey; // If set, route requires this discovery key to be present in cloud platform config
  requiresAuthzPermissionOR?: AuthzPermission[]; // User must have at least ONE of these permissions
  requiresAuthzPermissionAND?: AuthzPermission[]; // User must have ALL of these permissions
  requiresPermission?: (permissions: Permissions) => boolean;
}

const RootRedirect = () => {
  const env = useEnv();
  const user = useUser();
  const publicRoutesOnly = !!env?.publicModeEnabled && user.isPublic;
  const homePageAvailable = !publicRoutesOnly && env?.homePage?.enabled;

  return homePageAvailable ? <Redirect to={RoutePaths.HOME} /> : <Redirect to={RoutePaths.APPSTORE} />;
};

export const isHomePageAvailable = ({ env, user }: Partial<NavContext>) => {
  return !!(env?.homePage?.enabled && !user?.isPublic);
};

const HomePageRoute = () => {
  const env = useEnv();
  const user = useUser();

  const homePageAvailable = isHomePageAvailable({ env, user });

  return homePageAvailable ? <HomePage /> : <Redirect to={RoutePaths.APPSTORE} />;
};

export const ROUTE_DEFINITIONS: RouteDefinition[] = [
  // Public routes
  {
    path: RoutePaths.ROOT,
    exact: true,
    render: () => <RootRedirect />,
  },
  {
    path: RoutePaths.HOME,
    exact: true,
    requiresWorkspaces: true,
    render: () => <HomePageRoute />,
  },
  {
    path: RoutePaths.APPSTORE,
    exact: true,
    render: () => <AppStorePage />,
  },
  {
    path: RoutePaths.APP,
    exact: true,
    requiresWorkspaces: true,
    render: ({ match }: RouteComponentProps<{ id: string }>) => <AppDetailPage id={match.params.id} />,
  },

  // Authenticated routes
  {
    path: RoutePaths.INSTANCES,
    exact: true,
    requiresAuth: true,
    requiresWorkspaces: true,
    requiresEnvCondition: (env) => env.workspacesEnabled,
    render: () => <InstancePage />,
  },
  {
    path: RoutePaths.INSTANCELOG,
    exact: true,
    requiresAuth: true,
    render: ({ match }: RouteComponentProps<{ instanceId: string }>) => (
      <AppInstanceLogPage instanceId={match.params.instanceId} />
    ),
  },
  {
    path: RoutePaths.APP_ALIAS,
    exact: false,
    requiresAuth: true,
    render: ({ match }: RouteComponentProps<{ appName: string }>) => <AppAlias appName={match.params.appName} />,
  },
  {
    path: RoutePaths.CLI_AND_API_ACCESS,
    exact: true,
    requiresAuth: true,
    render: () => <CliAndApiAccessPage />,
  },
  {
    path: RoutePaths.LOGGING_SERVICES,
    exact: true,
    requiresAuth: true,
    render: () => <LoggingServicesPage />,
  },
  {
    path: RoutePaths.MYAPPS,
    exact: true,
    requiresAuth: true,
    requiresWorkspaces: true,
    requiresEnvCondition: (env) => !!env.menu?.hasMyApp,
    render: () => <MyAppsPage />,
  },
  {
    path: RoutePaths.MY_AI_ENGINES,
    exact: true,
    requiresAuth: true,
    requiresWorkspaces: true,
    requiresEnvCondition: (env) => !!env.menu?.hasAiEngineManager,
    requiresAuthzPermissionOR: createPermissions([
      AIEMUserActionType.DAI_ENGINE_LIST,
      AIEMUserActionType.H2O_ENGINE_LIST,
      AIEMUserActionType.NOTEBOOK_ENGINE_LIST,
    ]),
    render: () => <MyAIEnginesPage />,
  },
  {
    path: RoutePaths.LEGACY_ENGINE_LOG,
    exact: true,
    requiresAuth: true,
    requiresEnvCondition: (env) => !!env.menu?.hasAiEngineManager,
    render: ({ match }: RouteComponentProps<{ engineName: string; engineType: Engine_Type }>) => (
      <LegacyEngineLogPage engineName={match.params.engineName} engineType={match.params.engineType} />
    ),
  },
  {
    path: RoutePaths.LEGACY_ADMIN_ENGINE_LOG,
    exact: true,
    requiresAuth: true,
    requiresEnvCondition: (env) => !!env.menu?.hasAiEngineManager,
    render: ({ match }: RouteComponentProps<{ engineName: string; engineType: Engine_Type }>) => (
      <LegacyEngineLogPage engineName={match.params.engineName} engineType={match.params.engineType} />
    ),
  },
  {
    path: RoutePaths.SECURE_STORE_OAUTH_CLIENTS,
    exact: true,
    requiresAuth: true,
    requiresEnvCondition: (env) => !!env.menu?.hasSecureStore,
    render: () => <OAuthClientsPage />,
  },
  {
    path: RoutePaths.SECURE_STORE_SECRETS,
    exact: true,
    requiresAuth: true,
    requiresWorkspaces: true,
    requiresEnvCondition: (env) => !!env.menu?.hasSecureStore,
    render: () => <SecretServicePage />,
  },
  {
    path: RoutePaths.MANAGE_WORKSPACES,
    exact: true,
    requiresAuth: true,
    requiresWorkspaces: true,
    requiresEnvCondition: (env) => !(env.publicModeEnabled && env.menu?.user?.isPublic),
    requiresAuthzPermissionAND: createPermissions([WorkspacesUserActionType.WORKSPACE_LIST]),
    render: () => <MyWorkspaces />,
  },
  {
    path: RoutePaths.PLATFORM_USAGE,
    exact: true,
    requiresAuth: true,
    requiresEnvCondition: (env) => env.platformUsageEnabled,
    render: () => <PlatformUsage />,
  },
  {
    path: RoutePaths.AUDIT_TRAIL,
    exact: true,
    requiresAuth: true,
    requiresWorkspaces: true,
    requiresEnvCondition: (env) => !!env.menu?.hasAuditTrail,
    render: () => <AuditTrailPage />,
    requiresAuthzPermissionAND: createPermissions([AuditTrailUserActionType.AUDIT_TRAIL_SEARCH]),
  },
  {
    path: RoutePaths.DRIVE,
    exact: true,
    requiresAuth: true,
    requiresWorkspaces: true,
    requiresEnvCondition: (env) => !!env.menu?.driveEnabled,
    requiresAuthzPermissionAND: createPermissions([DriveUserActionType.LIST_OBJECTS]),
    render: () => <Drive />,
  },
  {
    path: RoutePaths.DRIVE_BUCKET,
    requiresAuth: true,
    requiresWorkspaces: true,
    requiresEnvCondition: (env) => !!env.menu?.driveEnabled,
    requiresAuthzPermissionAND: createPermissions([DriveUserActionType.LIST_OBJECTS]),
    render: () => <Drive />,
  },

  // Admin routes
  {
    path: RoutePaths.ADMIN_AI_ENGINES,
    exact: true,
    requiresAdmin: true,
    requiresEnvCondition: (env) => !!env.menu?.hasAiEngineManager,
    requiresAuthzPermissionOR: createPermissions([
      AIEMUserActionType.DAI_ENGINE_LIST,
      AIEMUserActionType.H2O_ENGINE_LIST,
      AIEMUserActionType.NOTEBOOK_ENGINE_LIST,
    ]),
    render: () => <AdminAIEnginesPage />,
  },
  {
    path: RoutePaths.AI_ENGINE_SETTINGS,
    exact: true,
    requiresAdmin: true,
    requiresEnvCondition: (env) => !!env.menu?.hasSuperAdminMenus && !!env.menu?.hasAiEngineManager,
    requiresAuthzPermissionOR: createPermissions([
      AIEMUserActionType.DAI_ENGINE_PROFILES_LIST,
      AIEMUserActionType.H2O_ENGINE_PROFILES_LIST,
      AIEMUserActionType.NOTEBOOK_ENGINE_PROFILES_LIST,
      AIEMUserActionType.DAI_ENGINE_VERSION_LIST,
      AIEMUserActionType.H2O_ENGINE_VERSION_LIST,
      AIEMUserActionType.NOTEBOOK_ENGINE_IMAGES_LIST,
      AIEMUserActionType.SANDBOX_ENGINE_IMAGES_LIST,
      AIEMUserActionType.SANDBOX_ENGINE_TEMPLATES_LIST,
    ]),
    render: () => <AIEMAdminSettingsPage />,
  },
  {
    path: RoutePaths.ADMIN_APPS,
    exact: true,
    requiresAdmin: true,
    requiresWorkspaces: true,
    render: () => <AdminAppsPage />,
  },
  {
    path: RoutePaths.ADMIN_ALIASES,
    exact: true,
    requiresAdmin: true,
    requiresWorkspaces: true,
    requiresEnvCondition: (env) => env.workspacesEnabled,
    render: () => <AdminManageAliasPage />,
  },
  {
    path: RoutePaths.ADMIN_INSTANCES,
    exact: true,
    requiresAdmin: true,
    render: () => <AdminInstancePage />,
  },
  {
    path: RoutePaths.ADMIN_INSTANCELOG,
    exact: true,
    requiresAdmin: true,
    render: ({ match }: RouteComponentProps<{ instanceId: string }>) => (
      <AppInstanceLogPage instanceId={match.params.instanceId} />
    ),
  },
  {
    path: RoutePaths.ADMIN_SECRETS,
    exact: true,
    requiresAdmin: true,
    render: () => <AdminManageSecretsPage />,
  },
  {
    path: RoutePaths.ADMIN_MARKETPLACE_APPS,
    exact: true,
    requiresAdmin: true,
    requiresEnvCondition: (env) => !!env.menu?.hasAdminMarketPlaceApps,
    render: () => <AdminMarketplaceAppListPage />,
  },
  {
    path: RoutePaths.ADMIN_MARKETPLACE_APP,
    exact: true,
    requiresAdmin: true,
    requiresEnvCondition: (env) => !!env.menu?.hasAdminMarketPlaceApps,
    render: ({ match }: RouteComponentProps<{ id: string }>) => <AdminMarketplaceAppDetailPage id={match.params.id} />,
  },
  {
    path: RoutePaths.ADMIN_MANAGE_WORKSPACES,
    exact: true,
    requiresAdmin: true,
    requiresAuthzPermissionAND: createPermissions([WorkspacesUserActionType.WORKSPACE_LIST_ALL]),
    render: () => <AdminWorkspaces />,
  },
  {
    path: RoutePaths.NOTEBOOK_SETTINGS,
    exact: true,
    requiresAdmin: true,
    requiresEnvCondition: (env) => !!env.menu?.hasNotebook && !!env.menu?.hasSuperAdminMenus,
    render: () => <NotebookAdminSettingsPage />,
  },
];

export const ALL_ROUTE_DEFINITIONS: RouteDefinition[] = [
  ...ROUTE_DEFINITIONS,
  ...ORCHESTRATOR_ROUTE_DEFINITIONS,
  ...ADMIN_CENTER_ROUTE_DEFINITIONS,
  ...FEATURESTORE_ROUTE_DEFINITIONS,
  ...MLOPS_ROUTE_DEFINITIONS,
];
